import React, { useEffect, useState } from 'react'
import isMobile from 'ismobilejs'
import { animateScroll as scroll } from 'react-scroll'
import VisibilitySensor from 'react-visibility-sensor'
import {
  cookie,
  parseTranslations,
  PublishedComponent,
  Routes,
  goTo,
  analytics,
  PointsBubble,
  canUseDOM
} from '@pdrj/pmi-iqos-shared-react'
import {
  BlockingLoader,
  Container,
  StepsProgress,
  TextBody,
  TextTitle,
  ToastContext,
  ListContainer,
  Image,
  Icon,
  Button,
  State,
  Css
} from '@pdrj/pmi-iqos-ds-react'
import {
  getInitialPropsMock,
  dispatchFullyEarnedEvent,
  dispatchPartiallyEarnedEvent
} from '@iqosphere-common/utils'
import {
  IqosphereApiManager,
  IqosphereAnalytics
} from '@iqosphere-common/services'
import QuizTranslations from './quiz.json'

const QuizComponent = ({ translations, quiz, line, mission, rewardEngine }) => {
  const [steps, setSteps] = useState()
  const [quizTemplate, setQuizTemplate] = useState()
  const [questions, setQuestions] = useState()
  const [currentQuestionId, setCurrentQuestionId] = useState()
  const [stateData, setStateData] = useState({})
  const [mappedData, setMappedData] = useState()
  const [loading, setLoading] = useState()
  const [submitResult, setSubmitResult] = useState()
  const [quizComplete, setQuizComplete] = useState()
  const [quizCompleteStatus, setQuizCompleteStatus] = useState(null)
  const [isTopVisible, setIsTopVisible] = useState(false)
  const [tx25QuizAnswerResult, setTx25QuizAnswerResult] = useState(false)
  const [tx25CorrectAnswer, setTx25CorrectAnswer] = useState('')
  const [submittingMissionResult, setSubmittingMissionResult] = useState(false)

  const views = {
    select: 'select',
    result: 'result'
  }
  const templates = {
    withImage: 'with_image',
    noImage: 'no_image',
    noImageTwoColls: 'no_image_2_col'
  }
  const quizCompleteTypes = {
    allCorrect: 'allCorrect',
    semiCorrect: 'semiCorrect',
    allWrong: 'allWrong'
  }
  const alignmentTypes = {
    left: 'left',
    center: 'center',
    right: 'right'
  }
  const rewardEngines = {
    ecom: 'ecom',
    loyalty: 'loyalty'
  }

  const theme = {
    light: 'light',
    dark: 'dark'
  }

  const [view, setView] = useState(views.select)
  const api = new IqosphereApiManager(line)
  const missionContext = !!(mission && mission.id)

  useEffect(() => {
    buildQuizState()
  }, [])

  const updateQuizCompleteStatus = (newSteps, questions) => {
    if (newSteps) {
      const correctAnswers = newSteps
        .map(step => step.answer)
        .filter(Boolean).length
      setQuizCompleteStatus(
        correctAnswers === questions.length
          ? quizCompleteTypes.allCorrect
          : correctAnswers === 0
          ? quizCompleteTypes.allWrong
          : quizCompleteTypes.semiCorrect
      )
    }
  }

  const popupToast = ToastContext.getContextActions().popupToast

  const getStepName = index => {
    return 'Q' + (index + 1)
  }
  const buildQuizState = () => {
    const data = quiz && {
      title: quiz.t,
      campaign: quiz.cmp,
      version: quiz.vn,
      questions: quiz.qtns.map(question => {
        return {
          index: question.q_i,
          name: question.q_n,
          instruction: question.q_ins,
          image: question.q_img,
          options: question.q_o,
          answer: question.q_a,
          description: question.q_d,
          descriptionExtra: question.q_de
        }
      }),
      qoins: quiz.qn,
      remainingQoins: quiz.rqn,
      template: quiz.tmp
    }
    if (data) {
      setMappedData(data)
      if (rewardEngine === rewardEngines.loyalty) {
        loadApiData(data)
      }
      setQuizTemplate(data.template)
      setQuestions(data.questions)
      if (missionContext) {
        setCurrentQuestionId(0)
        setTx25CorrectAnswer(
          data.questions[0].options.find(
            (option, index) => index === Number(data.questions[0].answer)
          )
        )
      }
    } else {
      popupToast({
        type: ToastContext.types.error,
        title: translations.tk_error
      })
    }
  }

  const resetQuiz = () => {
    setSteps(null)
    setQuizTemplate(null)
    setQuestions(null)
    setCurrentQuestionId(null)
    setStateData({})
    setMappedData(null)
    setLoading(null)
    setSubmitResult(null)
    setQuizComplete(null)
    setQuizCompleteStatus(null)
    buildQuizState()
    setView(views.select)
  }
  const loadApiData = async data => {
    try {
      setLoading(true)
      const apiQuizData = await api.iqosphereQuiz.getQuiz(
        data.campaign,
        data.version,
        false
      )

      const auxSteps = data.questions.map((question, index) => {
        return {
          stepName: getStepName(index),
          ...(index < apiQuizData.questionIndex && { confirmed: true })
        }
      })
      setSteps(auxSteps)
      updateQuizCompleteStatus(auxSteps, data.questions)
      apiQuizData.questionIndex > data.questions.length - 1
        ? setCurrentQuestionId(0)
        : setCurrentQuestionId(apiQuizData.questionIndex)
    } catch (err) {
      popupToast({
        type: ToastContext.types.error,
        title: translations.tk_error
      })
    } finally {
      setLoading(false)
    }
  }

  const handleError = () => {
    popupToast({
      type: ToastContext.types.error,
      subtitle: translations.tk_error
    })
  }

  const getQuizResponse = async (
    campaign,
    version,
    questionIdx,
    answerIdx,
    questions
  ) => {
    analytics.onClickQuizSubmit(mappedData.version, questionIdx)
    try {
      setLoading(true)
      const result = await api.iqosphereQuiz.submitQuizAnswer(
        campaign,
        version,
        questionIdx,
        answerIdx
      )

      const auxSteps = steps.map((step, index) => {
        return {
          stepName: getStepName(index),
          ...steps[index],
          ...(step.confirmed && { confirmed: step.confirmed }),
          ...(questionIdx === index && { confirmed: result.correct }),
          ...(questionIdx === index && { answer: result.correct })
        }
      })
      setSteps(auxSteps)
      dispatchPartiallyEarnedEvent()
      updateQuizCompleteStatus(auxSteps, questions)
      setSubmitResult(result)
      analytics.onClickQuizValidation(
        mappedData.version,
        currentQuestionId,
        result.correct
      )
    } catch (error) {
      handleError()
    } finally {
      setLoading(false)
    }
  }

  const buildFinishQuizState = finishQuizData => {
    return {
      ...finishQuizData,
      quiz: { ...finishQuizData.quiz },
      quizResultQoins: { ...finishQuizData.quizResultQoins }
    }
  }

  const completeQuiz = async (campaign, version) => {
    try {
      setLoading(true)
      const finishQuizData = await api.iqosphereQuiz.getQuiz(
        campaign,
        version,
        true
      )
      const mappedQuizData = buildFinishQuizState(finishQuizData)
      setQuizComplete(mappedQuizData)
      if (mappedQuizData.quizResultQoins.remaining === 0) {
        dispatchFullyEarnedEvent()
      } else {
        dispatchPartiallyEarnedEvent()
      }
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  const onRepeatQuizClick = () => {
    if (quizComplete.quizResultQoins.remaining === 0) {
      analytics.onClickGoToQuizPage()
      goTo(Routes.iqosphereQuizLP)
    } else {
      resetQuiz()
    }
  }

  const onRepeatLineQuizClick = () => {
    handleNotifyCorezoid('quiz')
    analytics.onClickGoToQuizPage()
  }

  const onPresentsClick = () => {
    if (line) {
      handleNotifyCorezoid('lotteryExchangeMenu')
    } else {
      goTo(Routes.iqospherePointsPresent)
    }
    analytics.onClickGoToPresents()
  }

  const handleTopVisibilityToggle = isVisible => {
    setIsTopVisible(!isVisible)
  }

  const addOptionState = (questionId, questionValue, optionId) => {
    const obj = { ...stateData }
    const isToggled =
      obj[questionId] && obj[questionId].selectedOption === optionId
    obj[questionId] = {}
    if (!isToggled) {
      obj[questionId].selectedOption = optionId
      obj[questionId].value = questionValue
    }

    setStateData(obj)
  }

  const selectQuestionOptionHandler = (questionId, questionValue, optionId) => {
    addOptionState(questionId, questionValue, optionId)
  }

  const updateCookie = async (
    sync = false,
    fetchNotices = false,
    logout = false
  ) => {
    await api.iqosphereAuth.getPosition(sync, fetchNotices, logout)
  }

  const validateQuestion = async () => {
    if (!isTopVisible) {
      scroll.scrollToTop({
        duration: 500
      })
    }
    if (view === views.select) {
      setView(views.result)
      const selectedOption = stateData[currentQuestionId]
      if (missionContext) {
        setTx25QuizAnswerResult(
          String(selectedOption.selectedOption) ===
            mappedData.questions[0].answer
        )
        setSubmitResult(selectedOption)
        IqosphereAnalytics.onContinueMissionQuiz(`${mission.id} - Submit`)
      } else {
        getQuizResponse(
          mappedData.campaign,
          mappedData.version,
          currentQuestionId,
          selectedOption.selectedOption,
          mappedData.questions
        )
      }
    } else {
      if (rewardEngine === rewardEngines.ecom) {
        setSubmittingMissionResult(true)
        const analyticsLabel = tx25QuizAnswerResult
          ? `${mission.id} - Correct Answer Result`
          : `${mission.id} - Wrong Answer Result`
        IqosphereAnalytics.onContinueMissionQuiz(analyticsLabel)
        try {
          const result = await api.iqosphereQuiz.submitActivityQuizAnswer(
            mappedData.campaign,
            mission.id,
            String(submitResult.selectedOption)
          )
          window.dispatchEvent(
            new window.CustomEvent('mcpMissions:reward:show', {
              detail: result
            })
          )
        } catch (error) {
          setSubmittingMissionResult(false)
        }
      } else {
        if (currentQuestionId < mappedData.questions.length - 1) {
          setCurrentQuestionId(currentQuestionId + 1)
          setView(views.select)
        } else {
          updateCookie()
          completeQuiz(mappedData.campaign, mappedData.version)
        }
      }
    }
  }

  const isSubmitEnabled = questionId => {
    return !!(stateData[questionId] && stateData[questionId].value)
  }

  const handleNotifyCorezoid = async type => {
    await api.profile.notifyCorezoid({ type, status: 'success' })
    canUseDOM && window.liff && window.liff.closeWindow()
  }

  const QuizTitle = ({ title, alignment }) => {
    return (
      <TextTitle
        heading={TextTitle.headings.h4}
        alignment={
          alignment === alignmentTypes.right
            ? TextTitle.alignments.right
            : alignment === alignmentTypes.center
            ? TextTitle.alignments.center
            : TextTitle.alignments.left
        }
        additionalClasses={
          missionContext && mission.theme === theme.dark
            ? 'q-text-turquoise'
            : ''
        }
        data-testing="Quiz_TextTitle_QuizTitle">
        {title}
      </TextTitle>
    )
  }

  const TX25Option = ({
    optionBoxCss = {},
    onOptionClickHandler,
    optionRadioCss = {},
    textColor,
    option,
    state
  }) => {
    return (
      <Css s={optionBoxCss}>
        <Container
          p="4"
          position={Container.positions.relative}
          display={Container.displays.flex}
          justifyContent={Container.justifyContentValues.around}
          alignItems={Container.alignItemsValues.center}
          fullWidth
          onClickHandler={onOptionClickHandler}>
          <Css s={optionRadioCss}>
            {state ? <State state={state} /> : <Container width="4" />}
          </Css>

          <Css s={{ flexBasis: `${isMobile().any ? '85%' : '92%'}` }}>
            <Container additionalClasses={textColor}>{option}</Container>
          </Css>
        </Container>
      </Css>
    )
  }

  const SelectableOption = ({ option, optionIndex }) => {
    const validated =
      stateData[currentQuestionId] &&
      stateData[currentQuestionId].selectedOption === optionIndex
    return missionContext ? (
      <Container
        fullWidth
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.between}
        alignItems={Container.alignItemsValues.start}>
        {mission.theme === theme.dark ? (
          <TX25Option
            optionBoxCss={{
              background: '#6D6D6D',
              width: '100%',
              borderRadius: '10px',
              border: '1.5px solid',
              borderColor: `${validated ? '#00D1D2' : '#6D6D6D'}`
            }}
            optionRadioCss={{
              flexBasis: '2%',
              border: '2px solid',
              borderColor: `${validated ? '#00D1D2' : '#2A2A2A'}`,
              borderRadius: '9999px',
              height: '16px',
              width: '16px'
            }}
            onOptionClickHandler={() =>
              selectQuestionOptionHandler(
                currentQuestionId,
                option,
                optionIndex
              )
            }
            option={option}
            textColor="q-text-white"
          />
        ) : mission.theme === theme.light ? (
          <TX25Option
            optionBoxCss={{
              background: '',
              width: '100%',
              borderRadius: '10px',
              border: '1.5px solid',
              borderColor: `${validated ? '#00D1D2' : '#6D6D6D'}`
            }}
            optionRadioCss={{
              flexBasis: '2%',
              border: '2px solid',
              borderColor: `${validated ? '#00D1D2' : '#2A2A2A'}`,
              borderRadius: '9999px',
              height: '16px',
              width: '16px'
            }}
            onOptionClickHandler={() =>
              selectQuestionOptionHandler(
                currentQuestionId,
                option,
                optionIndex
              )
            }
            option={option}
          />
        ) : null}
      </Container>
    ) : (
      <Container
        fullWidth
        display={Container.displays.flex}
        justifyContent={Container.justifyContentValues.between}
        alignItems={Container.alignItemsValues.start}>
        <Container
          pr="10"
          pl="4"
          py="6"
          position={Container.positions.relative}
          bg={validated ? null : Container.backgrounds.hardWhite}
          display={Container.displays.flex}
          justifyContent={Container.justifyContentValues.between}
          fullWidth
          onClickHandler={() =>
            selectQuestionOptionHandler(currentQuestionId, option, optionIndex)
          }
          borderColor={
            validated
              ? Container.borderColors.primary
              : Container.borderColors.default
          }>
          {option}
          <Container position={Container.positions.absolute} r="4">
            {validated && <State state={State.states.valid} />}
          </Container>
        </Container>
      </Container>
    )
  }

  const QuizCompleteTemplateBuilder = () => {
    const profileInfo = cookie.getTokenInfo()
    const isDR = profileInfo && profileInfo.has_device_registered === 'true'
    return (
      <>
        <Container
          display={Container.displays.flex}
          flexDirection={Container.flexDirections.column}
          alignItems={Container.alignItemsValues.center}>
          {quizCompleteStatus !== quizCompleteTypes.allWrong &&
          Number(quizComplete.quizResultQoins.earned) > 0 ? (
            <PointsBubble
              size={PointsBubble.sizes.l}
              headingLabel={translations.tk_aquisition}
              amount={quizComplete.quizResultQoins.earned.toString()}
              unitLabel={translations.tk_qoins}
            />
          ) : (
            quizCompleteStatus !== quizCompleteTypes.allWrong && (
              <State state={State.states.valid} size={State.sizes.xl} />
            )
          )}
          <Container py="4">
            <TextTitle heading={TextTitle.headings.h6}>
              {quizCompleteStatus !== quizCompleteTypes.allWrong
                ? translations.tk_quizCongratulation
                : translations.tk_challengeAgain}
            </TextTitle>
          </Container>
          {quizCompleteStatus !== quizCompleteTypes.allCorrect ? (
            <TextBody>
              {isDR
                ? quizComplete.quizResultQoins.max +
                  ' ' +
                  translations.tk_quizGetQoinsFirstPart +
                  ' ' +
                  quizComplete.quizResultQoins.remaining +
                  ' ' +
                  translations.tk_quizGetQoinsSecondPart
                : translations.tk_quizCompleteNDR}
            </TextBody>
          ) : null}
          <Container
            pt="8"
            display={Container.displays.flex}
            flexDirection={Container.flexDirections.column}
            alignItems={Container.alignItemsValues.center}>
            <Button
              onClickHandler={() =>
                line ? onRepeatLineQuizClick() : onRepeatQuizClick()
              }
              text={
                line
                  ? translations.tk_primaryCtaCloseLine
                  : quizComplete.quizResultQoins.remaining > 0
                  ? translations.tk_challengeAgain
                  : translations.tk_quizAnotherChallenge
              }
            />
            <Container
              pt="6"
              display={Container.displays.flex}
              justifyContent={Container.justifyContentValues.center}>
              <Button
                text={
                  line
                    ? translations.tk_secondaryCtaCloseLine
                    : translations.tk_checkPresents
                }
                variant={Button.variants.tertiary}
                onClickHandler={onPresentsClick}
                icon={Icon.images.arrowRight}
                iconPosition={Button.iconPositions.right}
              />
            </Container>
          </Container>
        </Container>
      </>
    )
  }

  const TX25QuizResultScreen = ({ resultIndicatorText, answerText, state }) => {
    return (
      <Container
        pb="4"
        display={Container.displays.flex}
        flexDirection={Container.flexDirections.column}
        alignItems={[
          Container.alignItemsValues.start,
          Container.alignItemsValues.center
        ]}
        fullWidth>
        <TextBody darkMode={mission.theme === theme.dark}>
          {resultIndicatorText}
        </TextBody>
        {mission.theme === theme.dark ? (
          <TX25Option
            optionBoxCss={{
              background: '#6D6D6D',
              minWidth: isMobile().any ? '100%' : '600px',
              marginTop: '12px',
              borderRadius: '10px',
              border: '1.5px solid',
              borderColor: `${
                state === State.states.wrongRed ? '#C73636' : '#00D1D2'
              }`
            }}
            optionRadioCss={{ flexBasis: '5%' }}
            textColor="q-text-white"
            option={answerText}
            state={state}
          />
        ) : mission.theme === theme.light ? (
          <TX25Option
            optionBoxCss={{
              background: '',
              minWidth: isMobile().any ? '100%' : '600px',
              marginTop: '12px',
              borderRadius: '10px',
              border: '1.5px solid',
              borderColor: `${
                state === State.states.wrongRed ? '#C73636' : '#00D1D2'
              }`
            }}
            optionRadioCss={{ flexBasis: '5%' }}
            option={answerText}
            state={state}
          />
        ) : null}
      </Container>
    )
  }

  const quizContentStyling = () => {
    const missionTheme = mission.theme
    switch (missionTheme) {
      case theme.dark:
        return {
          background:
            'linear-gradient(180deg, rgba(152, 152, 152, 0.2) 0%, rgba(50, 50, 50, 0.2) 100%)',
          padding: `${isMobile().any ? '20px 12px' : '40px'}`,
          borderRadius: '20px',
          border: '0.5px solid #FFFFFF80'
        }
      case theme.light:
        return {
          background: '',
          padding: `${isMobile().any ? '20px 12px' : '40px'}`,
          borderRadius: '20px',
          border: '0.5px solid #6D6D6D80'
        }
      default:
        return {}
    }
  }

  const QuizTemplate = ({ submitResult }) => {
    return (
      <ListContainer
        xBetween="8"
        yBetween="8"
        columns={
          quizTemplate === templates.withImage && !missionContext ? [1, 2] : 1
        }>
        {quizTemplate === templates.withImage && !missionContext && (
          <Image
            src={questions[currentQuestionId].image}
            height={['', '100']}
            width="100"
            contain
          />
        )}
        <Container
          display={Container.displays.flex}
          justifyContent={Container.justifyContentValues.center}>
          <Container
            display={
              view === views.select &&
              quizTemplate === templates.withImage &&
              !missionContext
                ? Container.displays.flex
                : Container.displays.block
            }
            alignItems={Container.alignItemsValues.center}
            width={
              quizTemplate === templates.noImage && !missionContext
                ? ['full', '1/2', '1/3']
                : 'full'
            }>
            {!loading ? (
              view === views.result && submitResult ? (
                missionContext ? (
                  <Container
                    display={Container.displays.flex}
                    flexDirection={Container.flexDirections.column}
                    alignItems={Container.alignItemsValues.center}>
                    {!tx25QuizAnswerResult && (
                      <TX25QuizResultScreen
                        resultIndicatorText={translations.tk_wrongAnswer}
                        answerText={submitResult.value}
                        state={State.states.wrongRed}
                      />
                    )}
                    <TX25QuizResultScreen
                      resultIndicatorText={
                        tx25QuizAnswerResult
                          ? translations.tk_correctAnswer
                          : translations.tk_correctAnswerIs
                      }
                      answerText={tx25CorrectAnswer}
                      state={State.states.valid}
                    />
                  </Container>
                ) : (
                  <Container>
                    <Container
                      display={Container.displays.flex}
                      alignItems={Container.alignItemsValues.center}
                      pb="8">
                      <Container pr="6">
                        {submitResult.correct ? (
                          submitResult.qoins > 0 ? (
                            <PointsBubble
                              size={PointsBubble.sizes.l}
                              headingLabel={translations.tk_aquisition}
                              amount={submitResult.qoins.toString()}
                              unitLabel={translations.tk_qoins}
                            />
                          ) : (
                            <State
                              state={State.states.valid}
                              size={State.sizes.xl}
                            />
                          )
                        ) : (
                          <State
                            state={State.states.wrong}
                            size={State.sizes.xl}
                          />
                        )}
                      </Container>
                      <Container>
                        <TextTitle heading={TextTitle.headings.h5}>
                          {submitResult.correct
                            ? translations.tk_correctAnswer
                            : translations.tk_wrongAnswer}
                        </TextTitle>
                      </Container>
                    </Container>
                    <Container pt="8" divider={Container.dividers.top}>
                      <TextTitle heading={TextTitle.headings.h4}>
                        {
                          questions[currentQuestionId].options[
                            submitResult.answer
                          ]
                        }
                      </TextTitle>
                    </Container>
                    <Container py="4">
                      <TextBody
                        value={questions[currentQuestionId].description}
                        acceptsHtml
                        data-testing="Quiz_TextBody_QuestionDescription"
                      />
                    </Container>
                    <TextBody
                      size={TextBody.sizes.xs}
                      value={questions[currentQuestionId].descriptionExtra}
                      acceptsHtml
                      data-testing="Quiz_TextBody_QuestionDescriptionExtra"
                    />
                  </Container>
                )
              ) : (
                <ListContainer
                  fullWidth
                  columns={
                    quizTemplate === templates.noImageTwoColls &&
                    !missionContext
                      ? 2
                      : missionContext
                      ? [1, 2]
                      : 1
                  }
                  yBetween="4"
                  xBetween={
                    quizTemplate === templates.noImageTwoColls || missionContext
                      ? '4'
                      : '0'
                  }
                  form={true}>
                  {questions[currentQuestionId].options.map(
                    (option, optionIndex) => (
                      <SelectableOption
                        key={optionIndex}
                        option={option}
                        optionIndex={optionIndex}
                      />
                    )
                  )}
                </ListContainer>
              )
            ) : null}
          </Container>
        </Container>
      </ListContainer>
    )
  }

  return (
    <Container position={Container.positions.relative}>
      <BlockingLoader loading={loading} />
      <>
        <VisibilitySensor resizeCheck onChange={handleTopVisibilityToggle}>
          <></>
        </VisibilitySensor>
        {!missionContext && (
          <Container pb={['8', '12']}>
            <QuizTitle
              title={mappedData && mappedData.title}
              alignment={alignmentTypes.center}
            />
          </Container>
        )}
        {steps && steps.length > 0 && !missionContext && (
          <Container pt="2" fullWidth={[true, false]}>
            <StepsProgress
              steps={steps}
              getStepText={() => ''}
              getStepContent={index => (
                <TextBody darkMode={index !== currentQuestionId}>
                  {steps[index].stepName}
                </TextBody>
              )}
              isStepCompleted={index =>
                (currentQuestionId &&
                  steps[index].confirmed !== undefined &&
                  currentQuestionId > index) ||
                steps[index].answer !== undefined
              }
              isStepValid={index =>
                (steps[index].answer && steps[index].confirmed) ||
                steps[index].confirmed
              }
              isStepHighlighted={index => index === currentQuestionId}
            />
          </Container>
        )}
        {quizComplete ? (
          <Container pt="12">
            <QuizCompleteTemplateBuilder />
          </Container>
        ) : (
          questions &&
          questions[currentQuestionId] && (
            <>
              <Css s={quizContentStyling()}>
                <Container pt={missionContext ? [] : ['8', '12']}>
                  <QuizTitle title={questions[currentQuestionId].name} />
                </Container>
                {!missionContext && (
                  <Container pt="4">
                    <TextBody
                      value={questions[currentQuestionId].instruction}
                      data-testing="Quiz_TextBody_QuestionInstruction"
                    />
                  </Container>
                )}
                <Container pt={['8', '12']}>
                  <QuizTemplate submitResult={submitResult} />
                </Container>
              </Css>
              <Container
                display={Container.displays.flex}
                justifyContent={Container.justifyContentValues.center}
                pt={['8', '12']}
                width={missionContext ? ['full', '1/2'] : ''}
                additionalClasses={
                  missionContext ? 'q-justify-self-center' : ''
                }>
                <Button
                  onClickHandler={validateQuestion}
                  disabled={
                    !isSubmitEnabled(currentQuestionId) ||
                    submittingMissionResult
                  }
                  loading={submittingMissionResult}
                  text={translations.tk_quizSubmit}
                  darkMode={missionContext && mission.theme === theme.dark}
                  fullWidth={missionContext}
                />
              </Container>
            </>
          )
        )}
      </>
    </Container>
  )
}

const Quiz = props => {
  return (
    <ToastContext>
      <PublishedComponent
        innerComponent={QuizComponent}
        {...ComponentDefaultProps}
        {...props}
        mainLayout
      />
    </ToastContext>
  )
}

const ComponentTranslations = {
  ...QuizTranslations
}

const ComponentDefaultProps = {
  componentName: 'Quiz',
  translations: {
    ...parseTranslations(ComponentTranslations, true)
  }
}

Quiz.getInitialProps = async req => {
  return PublishedComponent.getGlobalInitialProps({
    context: req && req.query && req.query.context,
    line: !!req?.query?.line,
    componentName: ComponentDefaultProps.componentName,
    defaultTranslations: ComponentDefaultProps.translations,
    translationsConfig: ComponentTranslations,
    getSpecificInitialProps: () => {
      const quiz =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('quiz')
          : req.body.extra && req.body.extra.quiz
      const mission =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('mission')
          : req.body.extra && req.body.extra.mission
      const rewardEngine =
        req && req.query && req.query.mock === 'true'
          ? getInitialPropsMock('quizRewardEngine')
          : req.body.extra && req.body.extra.quiz.re
      return {
        quiz,
        mission,
        rewardEngine
      }
    }
  })
}

export default Quiz
